import { css } from '@styled-system/css';
import { type FC } from 'react';

import { useNicheSoldProducts } from '@/features/analysisPageNiches/hooks/useNicheSoldProducts';
import type { AnalysisNiche } from '@/features/analysisPageNiches/types';
import { getFormattedNicheName } from '@/features/analysisPageNiches/utils/formatNicheName';
import { ProductFeed } from '@/shared/components/product/ProductFeed';
import { Button } from '@/shared/components/ui/Button';
import { MenuCloseButton } from '@/shared/components/ui/MenuCloseButton';
import { Message } from '@/shared/components/ui/Message';

export interface NicheSellsHistModalProps {
  analysisId: string;
  brandOrFullPath: string;
  nicheImageUrl?: string;
  niche: string;
  allNiches: AnalysisNiche[];
  onClose: () => void;
}

export const NicheSellsHistModal: FC<NicheSellsHistModalProps> = ({
  analysisId,
  brandOrFullPath,
  nicheImageUrl,
  niche,
  allNiches,
  onClose,
}) => {
  // Format the niche name using the same logic as the table
  const formattedNiche = getFormattedNicheName(niche, allNiches);
  // Récupération des produits du backend
  const {
    products: allProducts,
    isLoading: productsLoading,
    error: productsError,
  } = useNicheSoldProducts({
    analysisId: analysisId ?? '',
    brandOrFullPath,
    enabled: !!analysisId && !!brandOrFullPath,
  });

  const overlayStyles = css({
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'flex-end',
  });

  const modalStyles = css({
    position: 'relative',
    width: '100%',
    maxWidth: '850px',
    height: '100%',
    backgroundColor: 'base.bg',
    borderLeft: '0.75px solid',
    borderLeftColor: 'border.default',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    gap: 'sm',
  });

  const headerStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: 'sm',
    pt: 'md',
    px: 'md',
    flexShrink: 0,
  });

  const nicheImageStyles = css({
    width: '50px',
    height: '50px',
    borderRadius: 'lg',
    objectFit: 'cover',
    flexShrink: 0,
  });

  const nicheInfoStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '2xs',
    flex: 1,
  });

  const nicheNameStyles = css({
    fontSize: 'xl',
    fontWeight: 'bold',
    color: 'font.primary',
  });

  const nicheSubtitleStyles = css({
    fontSize: '2xs',
    fontWeight: 'semibold',
    color: 'font.lowkey',
  });

  const closeButtonStyles = css({
    flexShrink: 0,
  });

  const mobileCloseButtonPositionStyles = css({
    position: 'absolute',
    top: '35px',
    right: '18px',
  });

  const contentStyles = css({
    flex: 1,
    overflow: 'auto',
    p: 'md',
  });

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!analysisId) {
    return (
      <div className={overlayStyles} onClick={handleOverlayClick}>
        <div className={modalStyles}>
          <Message type="error" bordered>
            ID d'analyse manquant
          </Message>
        </div>
      </div>
    );
  }

  if (productsError) {
    return (
      <div className={overlayStyles} onClick={handleOverlayClick}>
        <div className={modalStyles}>
          <div className={headerStyles}>
            <div className={nicheInfoStyles}>
              <div className={nicheNameStyles}>
                {formattedNiche.path ? (
                  <>
                    <div
                      className={css({
                        fontSize: '2xs',
                        fontWeight: 'semibold',
                        color: 'font.lowkey',
                      })}
                    >
                      {formattedNiche.path}
                    </div>
                    <div>{formattedNiche.name}</div>
                  </>
                ) : (
                  formattedNiche.name
                )}
              </div>
              <div className={nicheSubtitleStyles}>Dernières ventes</div>
            </div>
            <div className={closeButtonStyles}>
              <Button handleClick={onClose}>Fermer</Button>
            </div>
          </div>
          <div className={contentStyles}>
            <Message type="error" bordered>
              {productsError}
            </Message>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={overlayStyles} onClick={handleOverlayClick}>
      <div className={modalStyles}>
        {/* Close Button */}
        <MenuCloseButton onClick={onClose} className={mobileCloseButtonPositionStyles} />

        {/* Header avec image et nom de la niche */}
        <div className={headerStyles}>
          {nicheImageUrl && <img src={nicheImageUrl} alt={formattedNiche.name} className={nicheImageStyles} />}
          <div className={nicheInfoStyles}>
            <div className={nicheNameStyles}>
              {formattedNiche.path ? (
                <>
                  <div
                    className={css({
                      fontSize: '2xs',
                      fontWeight: 'semibold',
                      color: 'font.lowkey',
                    })}
                  >
                    {formattedNiche.path}
                  </div>
                  <div>{formattedNiche.name}</div>
                </>
              ) : (
                formattedNiche.name
              )}
            </div>
            <div className={nicheSubtitleStyles}>Dernières ventes</div>
          </div>
        </div>

        {/* Product Feed */}
        <div className={contentStyles}>
          <ProductFeed allProducts={allProducts} status="sold" isLoading={productsLoading} />
        </div>
      </div>
    </div>
  );
};
